import React from "react";
import { trackEvent } from "@leafly-com/web-utils";
import { useSelector } from "react-redux";

import { Coordinates } from "custom-types/Coordinates";
import { MenuItem, MenuItemVariant } from "custom-types/MenuItem";
import { getDomainCountryCode } from "redux/selectors/config";
import { getCartId } from "redux/selectors/reservation";
import { getMenuItemUrl } from "utils/menuItemUrl";

import BigPrice from "components/BigPrice";
import Button from "components/botanic/Button";
import { useIsJaneCheckoutEnabled } from "components/Checkout/JaneCheckout";
import CartIcon from "components/Icons/cart.svg";

import { DispensaryInformation } from "./DispensaryInformation";
import { MenuItemHeader } from "./MenuItemHeader";
import { OneBagNotice } from "./OneBagNotice";
import { centsToDollars, formatCurrency } from "./utils/currency";
import { finalPrice, quantityLabel } from "./utils/variant";

export const noop = (): void => {
  /* do nothing */
};

type AddToBagConfirmationProps = {
  menuItem: MenuItem;
  onKeepShoppingClick: () => void;
  addedVariant: MenuItemVariant;
  quantity: number;
  userLocation?: Coordinates;
};

export const AddToBagConfirmation: React.FC<AddToBagConfirmationProps> = ({
  menuItem,
  onKeepShoppingClick = noop,
  addedVariant,
  quantity,
  userLocation,
}) => {
  const cartId = useSelector(getCartId);
  const countryCode = useSelector(getDomainCountryCode);

  const menuItemPath = getMenuItemUrl(menuItem, addedVariant.id);
  const isJaneCheckoutEnabled = useIsJaneCheckoutEnabled();
  return (
    <>
      <MenuItemHeader menuItem={menuItem} href={menuItemPath} className="mb-xl">
        <div className="font-bold mt-md">
          {quantityLabel(addedVariant, quantity)}
        </div>
        {addedVariant.deal && addedVariant.deal.active && (
          <div className="font-bold text-notification">
            {
              formatCurrency(
                centsToDollars(addedVariant.deal.totalSavings * quantity),
                countryCode
              )?.formattedPrice
            }{" "}
            savings
          </div>
        )}
        <div className="font-bold mt-md">
          <div className="mb-xs">Total Price</div>
          <BigPrice
            {...formatCurrency(finalPrice(addedVariant, quantity), countryCode)}
          />
        </div>
      </MenuItemHeader>

      <div className="flex mt-sm -mx-xs">
        <Button
          variant="tertiary"
          size="small"
          className="flex items-center justify-center flex-grow-0 w-full mx-xs"
          onClick={() => {
            onKeepShoppingClick();
            trackEvent("Add to Bag", "Click", "Keep Shopping", {
              cartId,
              janeCheckoutEnabled: isJaneCheckoutEnabled,
            });
          }}
        >
          <CartIcon height="16" width="16" className="mr-xs" />
          keep shopping
        </Button>

        <Button
          variant="secondary"
          size="small"
          className="flex items-center justify-center flex-grow-0 w-full mx-xs"
          onClick={() =>
            trackEvent("Add to Bag", "Click", "Checkout", {
              cartId,
              janeCheckoutEnabled: isJaneCheckoutEnabled,
            })
          }
          href="/bag"
        >
          check out
        </Button>
      </div>

      <DispensaryInformation
        countryCode={countryCode}
        dispensary={menuItem.dispensary}
        userLocation={userLocation}
      />

      <OneBagNotice onKeepShoppingClick={onKeepShoppingClick} />
    </>
  );
};
